import { Options } from "@api/models/HttpClientModels";
import OrderFoodRequest from "@components/order/models/edit/OrderFoodRequest";
import RootStore from "@stores/RootStore";
import { getIsDeliveryActive, getIsOpenTimeActive } from "@utils/restaurantHelper";
import { AxiosError } from "axios";
import _ from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import Food from "../models/Food";
import FoodAdditionalDrawerMode from "../models/FoodAdditionalDrawerMode";
import FoodCategory from "../models/FoodCategory";
import FoodPrice from "../models/FoodPrice";
import FoodSideDishFilter from "../models/FoodSideDishFilter";
import Restaurant from "../../restaurant/models/Restaurant";
import SideDish from "../models/SideDish";
import FoodSideDishService from "../services/FoodSideDishService";
import RestaurantOfferService from "../services/RestaurantOfferService";
import RestaurantService from "@components/restaurant/services/RestaurantService";

export default class RestaurantOfferStore {
    rootStore: RootStore;
    service: RestaurantOfferService;
    foodSideDishService: FoodSideDishService;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.service = new RestaurantOfferService();
        this.foodSideDishService = new FoodSideDishService();

        makeObservable(this, {
            onRestaurantOfferPageMount: action,
            onRestaurantOfferPageUnmount: action,

            //#region UI
            isLoading: observable,
            setIsLoading: action,
            //#endregion

            //#region Restaurant
            isRestaurantLoading: observable,
            setIsRestaurantLoading: action,
            selectedRestaurant: computed,
            restaurants: observable,
            setRestaurants: action,
            getRestaurantsAsync: action,
            onRestaurantClick: action,
            getRestaurantAsync: action,
            //#endregion

            //#region FoodCategory
            isFoodCategoryDrawerOpen: observable,
            setIsFoodCategoryDrawerOpen: action,

            isFoodCategoryLoading: observable,
            setIsFoodCategoryLoading: action,
            getFoodCategoriesAsync: action,
            selectedFoodCategory: computed,
            onFoodCategoryClick: action,
            //#endregion

            //#region Food
            isFoodLoading: observable,
            setIsFoodLoading: action,
            getFoodsAsync: action,
            onFoodClick: action,
            getFoodSideDishes: action,
            openFoodAdditionalDrawer: action,
            isFoodSideDishLoading: observable,
            setIsFoodSideDishLoading: action,
            isFoodAdditionalDrawerOpen: observable,
            setIsFoodAdditionalDrawerOpen: action,
            foodAdditionalDrawerMode: observable,
            setFoodAdditionalDrawerMode: action,
            isFoodNoteDialogOpen: observable,
            setIsFoodNoteDialogOpen: action,
            //#endregion

            //#region Food search
            foodSearchText: observable,
            setFoodSearchText: action,
            foodSearchResult: observable,
            setFoodSearchResult: action,
            onFoodSearch: action,
            clearFoodSearch: action,
            //#endregion

            //#region ClonedSelectedFood
            orderFoodRequest: observable,
            setOrderFoodRequest: action,
            increaseQuantity: action,
            decreaseQuantity: action,
            onFoodPriceClick: action,
            onSideDishClick: action,
            calculateSideDishPrice: action,
            updateFoodTotalPrice: action,
            onFoodNoteSave: action,
            //#endregion

            //#region Order
            onAddOrEditFoodClick: action,
            //#endregion
        })
    }

    onRestaurantOfferPageMount = async (restaurantId: string) => {
        var selectedRestaurant = this.selectedRestaurant;

        if (selectedRestaurant === undefined) {
            var restaurant = await this.getRestaurantAsync(restaurantId);

            if (restaurant !== undefined) {
                restaurant.isSelected = true;
                this.setRestaurants([restaurant]);
            }
            else {
                console.log("error while fetch restaurant");
            }
        }

        await this.getFoodCategoriesAsync(restaurantId);
    }

    onRestaurantOfferPageUnmount = async () => {
        var previouslySelectedFoodCategory = this.selectedFoodCategory;
        if (previouslySelectedFoodCategory !== undefined && previouslySelectedFoodCategory !== null) {
            previouslySelectedFoodCategory.isSelected = false;
        }

        var previouslySelectedRestaurant = this.selectedRestaurant;
        if (previouslySelectedRestaurant !== undefined && previouslySelectedRestaurant !== null) {
            previouslySelectedRestaurant.isSelected = false;
        }
    }

    //#region UI

    isLoading: boolean = false;
    setIsLoading = (value: boolean) => {
        this.isLoading = value
    }

    //#endregion

    //#region Restaurant

    isRestaurantLoading: boolean;
    setIsRestaurantLoading = (value: boolean) => {
        this.isRestaurantLoading = value;
    }

    restaurants: Restaurant[];
    setRestaurants = (restaurants: Restaurant[]) => {
        this.restaurants = restaurants;
    }
    get selectedRestaurant() {
        return this.restaurants?.find(r => r.isSelected);
    }

    getRestaurantsAsync = async () => {
        this.setIsRestaurantLoading(true);

        try {
            var city = this.rootStore.cityStore.selectedCity;
            var response = (await this.service.getRestaurantsAsync(city.id)).data;
            var restaurants = response.item;

            restaurants.forEach((restaurant) => {
                restaurant.isDeliveryActive = getIsDeliveryActive(restaurant);
                restaurant.isOpenTimeActive = getIsOpenTimeActive(restaurant);
            });

            this.setRestaurants(response.item);
        } catch (error) {
            if (error instanceof AxiosError) {

            }
        } finally {
            this.setIsRestaurantLoading(false);
        }
    }

    getRestaurantAsync = async (restaurantId: string) => {
        this.setIsRestaurantLoading(true);

        var restaurant: Restaurant | undefined;
        if (!_.isNil(this.restaurants)) {
            restaurant = this.restaurants.find(r => r.id === restaurantId);

            if (restaurant !== undefined) {
                return restaurant;
            }
        }

        try {
            var restaurantService = new RestaurantService();

            restaurant = (await restaurantService.getAsync(restaurantId)).data;

            restaurant.isDeliveryActive = getIsDeliveryActive(restaurant);
            restaurant.isOpenTimeActive = getIsOpenTimeActive(restaurant);

            return restaurant;
        } catch (error) {
            if (error instanceof AxiosError) {

            }
        } finally {
            this.setIsRestaurantLoading(false);
        }
    }

    onRestaurantClick = (restaurant: Restaurant) => {
        var previouslySelectedFoodCategory = this.selectedFoodCategory;
        if (previouslySelectedFoodCategory !== undefined && previouslySelectedFoodCategory !== null) {
            previouslySelectedFoodCategory.isSelected = false;
        }

        var previouslySelectedRestaurant = this.selectedRestaurant;
        if (previouslySelectedRestaurant !== undefined && previouslySelectedRestaurant !== null) {
            previouslySelectedRestaurant.isSelected = false;
        }

        restaurant.isSelected = true;

        if (restaurant.isOnline === false) {
            this.rootStore.toastStore.alert(
                "warning",
                `${restaurant.name} je trenutno izvan mreže.`,
                "Nećeš moći završiti svoju narudžbu.",
                10000
            )
        }

        this.rootStore.routerStore.navigate(`/restaurant/${restaurant.id}/offer`)
    }

    //#endregion

    //#region FoodCategory

    isFoodCategoryDrawerOpen: boolean;
    setIsFoodCategoryDrawerOpen = (value: boolean) => {
        this.isFoodCategoryDrawerOpen = value;
    }

    isFoodCategoryLoading: boolean;
    setIsFoodCategoryLoading = (value: boolean) => {
        this.isFoodCategoryLoading = value;
    }

    get selectedFoodCategory() {
        return this.selectedRestaurant?.foodCategories?.find(c => c.isSelected);
    }

    getFoodCategoriesAsync = async (restaurantId: string) => {
        this.setIsFoodCategoryLoading(true);

        try {
            var response = (await this.service.getFoodCategoriesAsync(restaurantId)).data;

            var selectedRestaurant = this.selectedRestaurant;

            if (selectedRestaurant === undefined) {

            } else {
                var foodCategories = response.foodCategories;

                foodCategories[0].isSelected = true;
                foodCategories.forEach(foodCategory => {
                    if (foodCategory.foods != undefined && foodCategory.foods.length > 0) {
                        foodCategory.foods.forEach((food) => {
                            food.foodPrices = _.orderBy(food.foodPrices, ["price"], "asc");
                        });
                    }
                });

                selectedRestaurant.foodCategories = foodCategories;
            }
        } catch (error) {
            if (error instanceof AxiosError) {

            }
        } finally {
            this.setIsFoodCategoryLoading(false);
        }
    }

    onFoodCategoryClick = async (foodCategory: FoodCategory) => {
        runInAction(() => {
            var previouslySelectedFoodCategory = this.selectedFoodCategory;
            if (previouslySelectedFoodCategory !== undefined && previouslySelectedFoodCategory !== null) {
                previouslySelectedFoodCategory.isSelected = false;
            }

            if (this.foodSearchResult !== undefined) {
                this.clearFoodSearch();
            }

            foodCategory.isSelected = true;
            this.setIsFoodCategoryDrawerOpen(false);
        })

        await this.getFoodsAsync(foodCategory);
    }

    //#endregion

    //#region Food

    isFoodLoading: boolean;
    setIsFoodLoading = (value: boolean) => {
        this.isFoodLoading = value;
    }

    isFoodSideDishLoading: boolean;
    setIsFoodSideDishLoading = (value: boolean) => {
        this.isFoodSideDishLoading = value;
    }

    isFoodAdditionalDrawerOpen: boolean = false;
    setIsFoodAdditionalDrawerOpen = (value: boolean) => {
        this.isFoodAdditionalDrawerOpen = value;
    }

    foodAdditionalDrawerMode: FoodAdditionalDrawerMode = "insert";
    setFoodAdditionalDrawerMode = (foodAdditionalDrawerMode: FoodAdditionalDrawerMode) => {
        this.foodAdditionalDrawerMode = foodAdditionalDrawerMode;
    }

    getFoodsAsync = async (foodCategory: FoodCategory) => {
        try {
            if (foodCategory.foods === undefined || foodCategory.foods.length < 1) {
                this.setIsFoodLoading(true);

                var response = (await this.service.getFoodsAsync(foodCategory.id)).data;

                var foods = response.item;
                if (foods.length > 0) {
                    foods.forEach((food) => {
                        food.foodPrices = _.orderBy(food.foodPrices, ["price"], "asc");
                    });
                }

                foodCategory.foods = response.item;
            }
        } catch (error) {
            if (error instanceof AxiosError) {

            }
        } finally {
            this.setIsFoodLoading(false);
        }
    }

    onFoodClick = async (food: Food) => {
        var clonedFood = _.cloneDeep(food);
        var defaultSelectedFoodPrice = clonedFood.foodPrices[0];
        defaultSelectedFoodPrice.isSelected = true;

        var orderFoodRequest: OrderFoodRequest = {
            foodId: food.id,
            food: clonedFood,
            quantity: 1,
            foodAmount: 0,
            totalAmount: 0,
            foodSize: defaultSelectedFoodPrice.size,
            note: null,
            sideDish: null,
            sideDishAmount: 0
        }

        await this.openFoodAdditionalDrawer(orderFoodRequest, "insert");

        food.sideDishes = orderFoodRequest.food.sideDishes;
    }

    getFoodSideDishes = async (orderFoodRequest: OrderFoodRequest) => {
        const food = orderFoodRequest.food;

        try {
            if (food.sideDishesEnabled && food.sideDishes === undefined || food.sideDishes === null || food.sideDishes.length < 1) {
                this.setIsFoodSideDishLoading(true);

                var filter: FoodSideDishFilter = {
                    foodIds: food.id
                };

                var options: Options = {
                    embed: "sideDish"
                };

                var response = (await this.foodSideDishService.findAsync(filter, {}, {}, options)).data;

                var foodSideDishes = response.item;

                var selectedSideDishIds = _.map(orderFoodRequest.sideDish, (s) => s.id);

                var sideDishes: SideDish[] = [];
                for (var foodSideDish of foodSideDishes) {
                    var sideDish: SideDish = foodSideDish.sideDish;
                    sideDish.isSelected = selectedSideDishIds.some(sideDishId => sideDishId === sideDish.id);
                    if (sideDish.isFree === false && sideDish.price > 0) {
                        sideDish.displayAmount = true;
                    }
                    else if (food.freeSideDishes === 0 && sideDish.price > 0) {
                        sideDish.displayAmount = true;
                    }
                    else {
                        sideDish.displayAmount = false;
                    }

                    sideDishes.push(sideDish);
                }

                var clonedSideDishes = _.cloneDeep(sideDishes)

                runInAction(() => {
                    food.sideDishes = clonedSideDishes;
                    this.orderFoodRequest.food.sideDishes = clonedSideDishes;
                })
            }
        } catch (error) {
            if (error instanceof AxiosError) {

            }
        } finally {
            this.setIsFoodSideDishLoading(false);
        }
    }

    openFoodAdditionalDrawer = async (orderFoodRequest: OrderFoodRequest, foodAdditionalDrawerMode: FoodAdditionalDrawerMode) => {
        runInAction(() => {
            this.setOrderFoodRequest(_.cloneDeep(orderFoodRequest));

            if (foodAdditionalDrawerMode === "insert") {
                this.updateFoodTotalPrice();
            }

            this.setFoodAdditionalDrawerMode(foodAdditionalDrawerMode);
            this.setIsFoodAdditionalDrawerOpen(true);
        })

        await this.getFoodSideDishes(orderFoodRequest);
    }

    isFoodNoteDialogOpen: boolean = false;
    setIsFoodNoteDialogOpen = (value: boolean) => {
        this.isFoodNoteDialogOpen = value;
    }

    //#endregion

    //#region Food search

    foodSearchText: string | undefined;
    setFoodSearchText = (value: string | undefined) => {
        this.foodSearchText = value;
    }

    foodSearchResult: Food[] | undefined;
    setFoodSearchResult = (foods: Food[] | undefined) => {
        this.foodSearchResult = foods;
    }

    onFoodSearch = async (value: string) => {
        this.setIsFoodLoading(true);
        try {
            var response = await this.service.getFoodsByNameAsync(this.selectedRestaurant!.id, value);

            runInAction(() => {
                this.setFoodSearchText(value);
                this.setFoodSearchResult(response.data.item);
            })

        } catch (error) {
            if (error instanceof AxiosError) {

            }
        } finally {
            this.setIsFoodLoading(false);
        }
    }

    clearFoodSearch = () => {
        runInAction(() => {
            this.setFoodSearchResult(undefined);
            this.setFoodSearchText(undefined);
        })
    }

    //#endregion

    //#region OrderFood

    orderFoodRequest: OrderFoodRequest;
    setOrderFoodRequest = (orderFoodRequest: OrderFoodRequest) => {
        this.orderFoodRequest = orderFoodRequest;
    }

    increaseQuantity = () => {
        this.orderFoodRequest.quantity++;
        this.updateFoodTotalPrice()
    }

    decreaseQuantity = () => {
        runInAction(() => {
            this.orderFoodRequest.quantity--;
            this.updateFoodTotalPrice()
        })
    }

    updateFoodTotalPrice = () => {
        var quantity = this.orderFoodRequest.quantity;

        var sideDishPrice = this.calculateSideDishPrice();
        var totalSideDishAmount = sideDishPrice * quantity
        this.orderFoodRequest.sideDishAmount = totalSideDishAmount;

        var foodPrice = this.orderFoodRequest.food.foodPrices.find(p => p.isSelected)?.price ?? 0;
        var totalFoodAmount = foodPrice * quantity;
        this.orderFoodRequest.foodAmount = totalFoodAmount;

        this.orderFoodRequest.totalAmount = totalFoodAmount + totalSideDishAmount;
    }

    onFoodPriceClick = (foodPrice: FoodPrice) => {
        runInAction(() => {
            var previouslySelectedFoodPrice = this.orderFoodRequest.food.foodPrices.find(p => p.isSelected);
            if (previouslySelectedFoodPrice !== undefined) {
                previouslySelectedFoodPrice.isSelected = false;
            }
            foodPrice.isSelected = true;
            this.updateFoodTotalPrice();
        })
    }

    onSideDishClick = (sideDish: SideDish) => {
        runInAction(() => {
            sideDish.isSelected = !sideDish.isSelected;
        })

        const userFreeSideDishes = this.orderFoodRequest.food.sideDishes.filter(
            (s) => s.isSelected === true && s.isFree === true && s.price > 0
        );
        var userFreeSideDishesCount = userFreeSideDishes.length;
        var foodFreeSideDishesCount = this.orderFoodRequest.food.freeSideDishes;

        if (userFreeSideDishesCount === foodFreeSideDishesCount) {
            for (var sideDish of this.orderFoodRequest.food.sideDishes) {
                if (sideDish.isFree && sideDish.isSelected === false && sideDish.price > 0) {
                    sideDish.displayAmount = true;
                }
                else {
                    if (sideDish.isFree) {
                        sideDish.displayAmount = false
                    }
                }
            }
        }
        else if (userFreeSideDishesCount < foodFreeSideDishesCount) {
            for (var sideDish of this.orderFoodRequest.food.sideDishes) {
                if (sideDish.isFree) {
                    sideDish.displayAmount = false
                }
            }
        }

        this.updateFoodTotalPrice();
    }

    calculateSideDishPrice = () => {
        let totalSideDishesAmount = 0;
        let selectedFreeSideDishCount = 0;

        var numberOfFoodFreeSideDishes = this.orderFoodRequest.food.freeSideDishes

        if (this.orderFoodRequest.food.sideDishes === undefined || this.orderFoodRequest.food.sideDishes === null) {
            return totalSideDishesAmount;
        }

        for (var sideDish of this.orderFoodRequest.food.sideDishes) {
            if (sideDish.isSelected === false) {
                continue;
            }

            if (sideDish.isFree === false) {
                totalSideDishesAmount += sideDish.price;
            } else if (sideDish.isFree && sideDish.price > 0) {
                if (selectedFreeSideDishCount >= numberOfFoodFreeSideDishes) {
                    totalSideDishesAmount += sideDish.price;
                }
                selectedFreeSideDishCount++;
            }
        }

        return totalSideDishesAmount;
    };

    onFoodNoteSave = (note: string | null) => {
        runInAction(() => {
            this.orderFoodRequest.note = note;
            this.setIsFoodNoteDialogOpen(false);
        })
    }

    //#endregion 

    //#region Order

    onAddOrEditFoodClick = () => {
        runInAction(() => {
            this.updateFoodTotalPrice();
            this.rootStore.orderEditViewStore.handleOnOrderFoodAddOrEdit(this.orderFoodRequest, this.foodAdditionalDrawerMode);
            this.setIsFoodAdditionalDrawerOpen(false);
        })
    }

    //#endregion

}
